import { useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, Plus, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useScrollIsolation } from '@/lib/useScrollIsolation'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { UserAvatar } from '@/components/composites/user-avatar'

/**
 * A picker you type into.
 *
 * The rule it exists to serve: a dropdown is only honest when its list is CLOSED and SHORT.
 * Priority has five values forever, so a select is right. People, teams and projects grow
 * with every hire and every deal, so scrolling one gets worse every month — those get this.
 *
 * Single or multiple, one component, because a watcher list and a team picker differ only in
 * how many answers they accept.
 */

export interface PickerOption {
  id: string
  label: string
  /** A second line in the list — a project's client, a team's parent path. */
  hint?: string
  /** Renders an avatar instead of a dot. */
  photoUrl?: string | null
  /** Groups options under a heading; omit for a flat list. */
  group?: string
}

interface BaseProps {
  options: PickerOption[]
  placeholder?: string
  /** Shown instead of the list while the options are still loading. */
  pending?: boolean
  disabled?: boolean
  invalid?: boolean
  id?: string
  /** Explains an empty list — "this project has no teams yet" beats "No matches". */
  emptyHint?: string
  /** Extra classes for the trigger — height and width, where it sits in a toolbar. */
  className?: string
  /** Multi-selects normally spell their choices out below. Toolbars can own those chips. */
  showChips?: boolean
  /** Put option hints below the label instead of squeezing both onto one line. */
  stackHints?: boolean
  /**
   * Offers a way out of the list.
   *
   * The dead end this fixes: a form REQUIRES a project, the project does not exist yet, and
   * the picker's advice is to go and open one on another tab — abandoning everything typed
   * so far. The answer belongs where the question is asked.
   */
  createLabel?: string
  onCreate?: () => void
  /**
   * Offers "select every one of these" on a multi-select.
   *
   * For lists where taking all of them is a real answer rather than an accident — the teams
   * delivering a project, say. It replaces pre-ticking them for you, which read as a
   * decision somebody had made when it was a default nobody chose.
   */
  selectAllLabel?: string
}

type SingleProps = BaseProps & {
  multiple?: false
  value: string
  onChange: (id: string) => void
}

type MultiProps = BaseProps & {
  multiple: true
  value: string[]
  onChange: (ids: string[]) => void
}

export function SearchSelect(props: SingleProps | MultiProps) {
  const { options, placeholder = 'Search…', pending, disabled, invalid, id, emptyHint, className, showChips = true, stackHints = false, createLabel, onCreate, selectAllLabel } =
    props
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const selected = props.multiple ? props.value : props.value ? [props.value] : []
  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options])
  // The list floats above a modal, so the dialog's scroll lock cancels wheel AND touch
  // scrolling in it unless the gesture is kept away from the document listener. See the hook.
  const listRef = useScrollIsolation<HTMLDivElement>()

  const q = query.trim().toLowerCase()
  /*
   * Every match, not the first fifty.
   *
   * The cap was invisible: option fifty-one simply did not exist, with nothing saying so, so
   * the honest reading of an absent team or timezone was "we do not have one". A list that
   * scrolls is a list you can reach the end of; a truncated one is a list that lies.
   *
   * The pickers that use this run to a few hundred options at most — the timezone list is
   * the largest at roughly four hundred — which a browser renders without complaint inside
   * an already-scrolling container. If one ever grows past what that can carry, the answer
   * is virtualisation, not hiding the tail.
   */
  const matches = useMemo(
    () =>
      q
        ? options.filter(
            (o) => o.label.toLowerCase().includes(q) || (o.hint ?? '').toLowerCase().includes(q),
          )
        : options,
    [options, q],
  )

  // Preserve the given order of groups rather than sorting them alphabetically — the caller
  // ordered them by how often each is the thing someone means.
  const groups = useMemo(() => {
    const out: Array<{ name: string | undefined; items: PickerOption[] }> = []
    for (const option of matches) {
      const existing = out.find((g) => g.name === option.group)
      if (existing) existing.items.push(option)
      else out.push({ name: option.group, items: [option] })
    }
    return out
  }, [matches])

  const pick = (optionId: string) => {
    if (props.multiple) {
      const next = props.value.includes(optionId)
        ? props.value.filter((v) => v !== optionId)
        : [...props.value, optionId]
      props.onChange(next)
      // Multi stays open: choosing three teams should not mean opening it three times.
    } else {
      props.onChange(optionId)
      setOpen(false)
      setQuery('')
    }
  }

  return (
    <div className="grid gap-1.5">
      <Popover open={open && !disabled} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <button
            id={id}
            type="button"
            disabled={disabled}
            aria-invalid={invalid}
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={() => {
              setOpen(true)
              // Focus after the popover mounts, so typing starts immediately.
              setTimeout(() => inputRef.current?.focus(), 0)
            }}
            className={cn(
              'flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm',
              'disabled:cursor-not-allowed disabled:opacity-50',
              invalid && 'border-danger',
              className,
            )}
          >
            <span
              className={cn(
                'min-w-0 truncate',
                selected.length === 0 && 'text-muted-foreground',
              )}
            >
              {pending
                ? 'Loading…'
                : selected.length === 0
                  ? placeholder
                  : props.multiple
                    ? selected.length <= 2
                      ? selected.map((selectedId) => byId.get(selectedId)?.label ?? selectedId).join(', ')
                      : `${selected.length} selected`
                    : (byId.get(selected[0] ?? '')?.label ?? '…')}
            </span>
            <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden />
          </button>
        </PopoverAnchor>

        <PopoverContent align="start" className="w-(--radix-popover-trigger-width) min-w-64 p-0">
          <div className="flex items-center gap-2 border-b border-border px-2.5">
            <Search className="size-3.5 shrink-0 text-text-muted" aria-hidden />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setOpen(false)
                // Enter takes the single best match, so a full type-and-go never needs the mouse.
                if (e.key === 'Enter' && matches[0]) {
                  e.preventDefault()
                  pick(matches[0].id)
                }
              }}
              placeholder="Type to search…"
              aria-label="Search options"
              className="h-9 w-full bg-transparent text-xs outline-none placeholder:text-text-muted"
            />
          </div>

          <div
            ref={listRef}
            className="max-h-80 overflow-y-auto overscroll-contain py-1"
          >
            {matches.length === 0 ? (
              <p className="px-3 py-2.5 text-xs text-text-muted">
                {options.length === 0 ? (emptyHint ?? 'Nothing to choose from yet.') : `Nothing matches “${query}”.`}
              </p>
            ) : null}

            {groups.map((group) => (
              <div key={group.name ?? '_'} className="py-0.5">
                {group.name ? (
                  <p className="px-3 pb-1 text-micro font-semibold tracking-wide text-text-muted uppercase">
                    {group.name}
                  </p>
                ) : null}
                {group.items.map((option) => {
                  const on = selected.includes(option.id)
                  return (
                    <button
                      key={option.id}
                      type="button"
                      role="option"
                      aria-selected={on}
                      // mousedown, not click: the input blurs first otherwise and the popover
                      // closes before the click lands.
                      onMouseDown={(e) => {
                        e.preventDefault()
                        pick(option.id)
                      }}
                      className={cn(
                        'flex w-full gap-2 px-3 text-left text-xs hover:bg-accent',
                        stackHints ? 'items-start py-2' : 'items-center py-1.5',
                      )}
                    >
                      {option.photoUrl !== undefined ? (
                        <UserAvatar
                          person={{ id: option.id, displayName: option.label, photoUrl: option.photoUrl }}
                          size="xs"
                        />
                      ) : null}
                      {stackHints ? (
                        <span className="min-w-0 flex-1">
                          <span className="block whitespace-normal font-medium leading-4">
                            {option.label}
                          </span>
                          {option.hint ? (
                            <span className="mt-0.5 block whitespace-normal leading-4 text-text-muted">
                              {option.hint}
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        <>
                          <span className="min-w-0 truncate">{option.label}</span>
                          {option.hint ? (
                            <span className="ml-auto truncate pl-2 text-text-muted">{option.hint}</span>
                          ) : null}
                        </>
                      )}
                      {on ? <Check className="ml-auto size-3.5 shrink-0 text-brand" aria-hidden /> : null}
                    </button>
                  )
                })}
              </div>
            ))}
          </div>

          {props.multiple && selectAllLabel && matches.length > 0 ? (
            <button
              type="button"
              // mousedown, like the options: the input blurs first otherwise and the popover
              // closes before the click lands.
              onMouseDown={(e) => {
                e.preventDefault()
                const all = matches.map((o) => o.id)
                const everyOneChosen = all.every((id) => selected.includes(id))
                // A toggle, so the same control undoes itself — and scoped to what the
                // search is currently showing, so "all" means the list in front of you.
                props.onChange(
                  everyOneChosen
                    ? selected.filter((id) => !all.includes(id))
                    : [...new Set([...selected, ...all])],
                )
              }}
              className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-xs font-medium text-brand hover:bg-accent"
            >
              <Check className="size-3.5 shrink-0" aria-hidden />
              {matches.every((o) => selected.includes(o.id)) ? 'Clear all' : selectAllLabel}
            </button>
          ) : null}

          {onCreate ? (
            <button
              type="button"
              // mousedown for the same reason the options use it: the search input blurs
              // first otherwise and the popover closes before the click lands.
              onMouseDown={(e) => {
                e.preventDefault()
                setOpen(false)
                setQuery('')
                onCreate()
              }}
              className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-xs font-medium text-brand hover:bg-accent"
            >
              <Plus className="size-3.5 shrink-0" aria-hidden />
              {createLabel ?? 'Create a new one'}
            </button>
          ) : null}
        </PopoverContent>
      </Popover>

      {/* What is chosen, spelled out. "3 selected" in the trigger is a count, not an answer. */}
      {props.multiple && showChips && selected.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((selectedId) => (
            <button
              key={selectedId}
              type="button"
              onClick={() => props.onChange(props.value.filter((v) => v !== selectedId))}
              className="flex h-6 items-center gap-1 rounded-full border border-brand bg-brand-soft px-2 text-tiny text-brand-dark transition-colors dur-fast hover:bg-brand-soft/70"
            >
              {byId.get(selectedId)?.label ?? selectedId}
              <X className="size-3" />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
